import type { Handler } from './handler';
import type {
  ApiFetchBlogPostsParams,
  ApiFetchBlogPostsPort,
} from './ports/api-fetch-blog-posts.port';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';

const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CACHE_ENTRIES = 10;

type CacheEntry = { data: BlogCore.Category[]; cachedAt: number };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<BlogCore.Category[]>>();

function getCacheKey(params: ApiFetchBlogPostsParams): string {
  return `${params.category ?? ''}|${params.state ?? ''}`;
}

export const fetchBlogPostsHandler: Handler<Params, Ports, Result> = async (
  params: ApiFetchBlogPostsParams = {},
  ports = {
    apiFetchBlogPosts: resolveAdapter('apiFetchBlogPosts'),
  },
) => {
  const key = getCacheKey(params);
  const cached = cache.get(key);

  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return { categories: cached.data };
  }

  const pending = inflight.get(key);

  if (pending) {
    const [data, err] = await tryCatch(pending);

    if (!err && data) return { categories: data };
  }

  const promise = ports?.apiFetchBlogPosts.handle(params);

  inflight.set(key, promise);

  const [categories, fetchError] = await tryCatch(promise);

  inflight.delete(key);

  if (!fetchError && categories) {
    if (cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;

      if (oldestKey) cache.delete(oldestKey);
    }

    cache.set(key, { data: categories, cachedAt: Date.now() });

    return { categories };
  }

  console.error('[fetchBlogPostsHandler] failed to fetch:', fetchError);

  if (cached) return { categories: cached.data };

  return { categories: [] };
};

export function clearBlogPostsCache(): void {
  cache.clear();
  inflight.clear();
}

type Params = ApiFetchBlogPostsParams;

type Ports = { apiFetchBlogPosts: ApiFetchBlogPostsPort };

type Result = Promise<{ categories: BlogCore.Category[] }>;
